import React, { useMemo } from 'react';
import { Form, Select, Space, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Service } from '@/api/mcp-appointments';
import { getPrimaryResourceCandidates } from '@/utils/serviceBooking';
import { getServiceRoleLabel } from '@/utils/serviceRoles';
import { getResourceDisplayName } from '@/utils/serviceResourceLinks';

const { Option } = Select;
const { Text } = Typography;

interface PrimaryResourceSelectorProps {
  service: Service | null | undefined;
}

export const PrimaryResourceSelector: React.FC<PrimaryResourceSelectorProps> = ({ service }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const candidates = useMemo(
    () => (service ? getPrimaryResourceCandidates(service) : []),
    [service]
  );

  if (!service || candidates.length === 0) {
    return (
      <Form.Item>
        <Typography.Text type="warning" style={{ fontSize: token.fontSizeSM }}>
          {t('appointmentComponents.create.primaryResource.noCandidates')}
        </Typography.Text>
      </Form.Item>
    );
  }

  return (
    <Form.Item
      label={t('appointmentComponents.create.primaryResource.label')}
      name="primaryResourceId"
      rules={[{ required: true, message: t('appointmentComponents.create.primaryResource.required') }]}
    >
      <Select
        placeholder={t('appointmentComponents.create.primaryResource.placeholder')}
        size="middle"
        showSearch
        optionFilterProp="label"
      >
        {candidates.map((link) => (
          <Option
            key={link.resourceId}
            value={link.resourceId}
            label={getResourceDisplayName(link.resource, link.resourceId)}
          >
            <Space>
              <span>{getResourceDisplayName(link.resource, link.resourceId)}</span>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                ({getServiceRoleLabel(link.serviceRole)})
              </Text>
            </Space>
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};
